// Reusable OnLogic logo image. Uses the official horizontal wordmark.
// Variant chooses the color/white version for light vs dark backgrounds.

import clsx from 'clsx';

interface LogoProps {
  variant?: 'color' | 'white';
  /** Height in pixels. Width auto-scales to preserve aspect ratio. */
  height?: number;
  className?: string;
}

// Intrinsic pixel dimensions of each wordmark PNG in apps/web/public. Supplying
// them as explicit width/height attributes lets the browser reserve correctly
// proportioned layout space *before* the image downloads — this clears the
// Lighthouse "unsized image" audit and removes the logo's layout-shift (CLS)
// contribution (IMP-046). The two variants have slightly different aspect
// ratios, so the values are per-variant. The inline `style` (fixed height +
// width:auto) still governs the rendered size; these attributes only establish
// the aspect ratio. Keep in sync if the source PNGs are ever re-exported.
//
// The colour wordmark is the vector file Marketing supplied
// (assets/logos/logo-onlogic-horizontal.svg, copied verbatim; IMP-062): 1.9 KB,
// sharp at any size and density, including the printed purchase order, and
// nothing to decode — the full-resolution PNG it replaces was 4502 px wide,
// ~84 KB and about 20 MB of decoded bitmap for a 48 px boot splash. Its
// width/height here are the SVG's viewBox. The white variant has no vector
// source yet, so it stays a PNG, re-exported 256 px tall from the brand
// original as an indexed-colour file; nothing renders it today.
const LOGO_ASSETS = {
  color: { src: '/onlogic-logo.svg', width: 1160, height: 302 },
  white: { src: '/onlogic-logo-white.png', width: 994, height: 256 },
} as const;

export function Logo({ variant = 'color', height = 28, className }: LogoProps) {
  const asset = LOGO_ASSETS[variant];
  return (
    <img
      src={asset.src}
      alt="OnLogic"
      width={asset.width}
      height={asset.height}
      className={clsx('select-none', className)}
      style={{ height, width: 'auto' }}
      draggable={false}
    />
  );
}
